import { z } from 'zod';
import type { CallLock, CallOffer, GameEvent, GameMeta, Reaction, Resolution, Take } from './contracts.js';

export const DIRECTOR_VERSION = 'director.v1';
/**
 * How the fan wants the show directed. The model writes a genuinely different script per
 * style/energy/length; the voice performs it. Facts are still verified server-side.
 */
export const DIRECTOR_STYLES = ['play_by_play', 'analyst', 'storyteller', 'fan', 'explain'] as const;
export const DIRECTOR_FOCUSES = ['drama', 'my-team', 'players', 'analysis', 'my-calls'] as const;
export type DirectorFocus = (typeof DIRECTOR_FOCUSES)[number];
/** through-cutoff = catch me up to now (no spoilers past what the fan has reached); complete = full finished-game story. */
export const DIRECTOR_SPOILERS = ['through-cutoff', 'complete'] as const;
export type DirectorSpoilers = (typeof DIRECTOR_SPOILERS)[number];
export const DIRECTOR_ENERGIES = ['calm', 'balanced', 'electric'] as const;
export const DIRECTOR_LENGTHS = ['short', 'standard', 'extended'] as const;
export type DirectorStyle = (typeof DIRECTOR_STYLES)[number];
export type DirectorEnergy = (typeof DIRECTOR_ENERGIES)[number];
export type DirectorLength = (typeof DIRECTOR_LENGTHS)[number];
/** Moments per show length (the planner may choose fewer when the game supports fewer). */
export const LENGTH_MOMENTS: Record<DirectorLength, number> = { short: 2, standard: 3, extended: 6 };
export const DirectorShowSchema = z.object({
  style: z.enum(DIRECTOR_STYLES).default('play_by_play'),
  energy: z.enum(DIRECTOR_ENERGIES).default('balanced'),
  length: z.enum(DIRECTOR_LENGTHS).default('standard'),
  /** Editorial lens the model directs through. */
  focus: z.enum(DIRECTOR_FOCUSES).default('drama'),
  spoilers: z.enum(DIRECTOR_SPOILERS).default('through-cutoff'),
  /** A voice from GET /director/voices; omitted = the server's default voice for the language. */
  voiceId: z.string().regex(/^[A-Za-z0-9_-]{4,64}$/).optional(),
});
export type DirectorShow = z.infer<typeof DirectorShowSchema>;
/** Effective show settings: request override › saved profile › defaults. */
export function showOf(profile?: { show?: Partial<DirectorShow> }, override?: Partial<DirectorShow>): DirectorShow {
  return DirectorShowSchema.parse({ ...(profile?.show ?? {}), ...(override ?? {}) });
}

/** A selectable narration voice from the speech provider's catalog. */
export type DirectorVoice = {
  voiceId: string;
  name: string;
  provider: 'elevenlabs';
  previewUrl?: string; // provider-hosted sample; play directly
  description?: string;
  accent?: string;
  gender?: string;
  age?: string;
  useCase?: string;
  /** Languages the provider's model can speak with this voice (multilingual models cover all three). */
  languages: DirectorLanguage[];
};

export const DirectorProfileSchema = z.object({
  followedTeams: z.array(z.string().regex(/^[A-Z]{3}$/)).max(30).default([]),
  followedPlayers: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
  language: z.enum(['en', 'hi', 'zh-Hans']).default('en'),
  familiarity: z.enum(['new', 'regular', 'expert']).default('regular'),
  /** Optional for profiles saved before show settings existed; read through showOf(). */
  show: DirectorShowSchema.optional(),
});
export type DirectorProfile = z.infer<typeof DirectorProfileSchema>;
export type DirectorLanguage = DirectorProfile['language'];
/** Chinese is optional on previously saved stories; new core templates provide all three. */
export type LocalizedText = { en: string; hi: string; 'zh-Hans'?: string };
export type DirectorLens = 'game' | 'team' | 'player' | 'call' | 'reaction';
export type DirectorOrder = 'chronological' | 'climax_first';
export type DirectorNarration = { introduction: LocalizedText; conclusion: LocalizedText };
/** A projection of an actual, validated event; never inferred motion or player tracking. */
export type DirectorVisual = Pick<GameEvent, 'eventId' | 'seq' | 'type' | 'period' | 'clockSec' | 'homeScore' | 'awayScore' | 'side' | 'personId' | 'made' | 'points' | 'x' | 'y'>;

export const DirectorRequestSchema = z.object({
  fanId: z.string().min(1).max(64),
  asOfSeq: z.number().int().min(-1).optional(),
  language: z.enum(['en', 'hi', 'zh-Hans']).optional(),
  maxMoments: z.number().int().min(1).max(6).default(3),
  /** Overrides the saved profile show settings for this request. */
  show: DirectorShowSchema.partial().optional(),
  /** Flagship path: a model-authored show or a typed error (AI_DIRECTOR_UNAVAILABLE / DIRECTOR_VALIDATION_FAILED) — never a template. */
  require: z.literal('model').optional(),
}).strict();

/** Typed failures for the flagship Director path (canonical spec rule 3: no silent downgrade). */
export type DirectorErrorCode = 'AI_DIRECTOR_UNAVAILABLE' | 'DIRECTOR_VALIDATION_FAILED' | 'AUDIO_UNAVAILABLE' | 'UNSUPPORTED_LOCALE';
export type DirectorError = { code: DirectorErrorCode; message: string; retriable: boolean; missing?: string[]; reasons?: string[] };

/** The AI producer's visual direction; every reference must resolve to supplied evidence. */
export const VISUAL_CUE_TYPES = ['player', 'score-change', 'court-event', 'momentum', 'official-embed', 'fan-call', 'reaction', 'title', 'silence'] as const;
export type VisualCue = { type: (typeof VISUAL_CUE_TYPES)[number]; eventId?: string; personId?: number; assetId?: string; instructions?: string };
export type ShowChapter = {
  segmentId: string; // matches DirectorResponse.segments[].segmentId
  sceneRole: 'setup' | 'rising-action' | 'turn' | 'climax' | 'aftermath';
  heading: string; // narrative chapter label, localized
  onScreenText: string; // short on-screen line, localized
  commentaryIntent: string;
  emphasis: 'quiet' | 'build' | 'excited' | 'reflective';
  visualCues: VisualCue[];
  targetDurationSec: number;
  personalizationWhy: string;
};
/** Model-authored show direction (thesis, arc, scenes, end card). Server-validated; never template-filled. */
export type ShowPlan = {
  thesis: string;
  storyArc: 'comeback' | 'collapse' | 'duel' | 'breakout' | 'clutch' | 'other';
  perspective: string;
  viewerReasonToCare: string;
  chapters: ShowChapter[];
  endCard: { title: string; text: string; verifiedCallId?: string; nextAction: string };
  factualValidation: { valid: boolean; errors: string[] };
};

/** Server-owned facts only. Never populate this from request JSON. */
export type DirectorInput = {
  roomId: string;
  meta: GameMeta;
  events: readonly GameEvent[];
  /** Immutable as-seen anchors for the stored offer/lock, separate from corrected current play evidence. */
  recordedCallEvents?: readonly GameEvent[];
  asOfSeq: number;
  fanId: string;
  profile: DirectorProfile;
  call?: CallOffer;
  locks: readonly CallLock[];
  resolution?: Resolution;
  takes: readonly Take[];
  reactions: readonly Reaction[];
  historyReliable: boolean;
  /** Last explicitly acknowledged catch-up in this room, never inferred from a generated response. */
  seenThroughSeq?: number;
};

export type DirectorMoment = {
  momentId: string;
  kind: 'score' | 'run' | 'lead_change' | 'comeback' | 'player' | 'call' | 'reaction' | 'finish';
  lens: DirectorLens;
  rankScore: number;
  reasons: string[];
  eventIds: string[];
  seq: number;
  period: number;
  clockSec: number;
  homeScore: number;
  awayScore: number;
  player?: { personId: number; name: string };
  visual?: DirectorVisual;
  /** Score states of the cited events, including a run's starting score. */
  scoreHistory?: Pick<DirectorVisual, 'eventId' | 'seq' | 'period' | 'clockSec' | 'awayScore' | 'homeScore'>[];
  title: LocalizedText;
  text: LocalizedText;
};

export type DirectorCallEvidence = {
  callId: string;
  choice: 'yes' | 'no';
  chosenTeam: string;
  lockedAtSeq: number;
  lockedAtEventId?: string;
  lockedAt: { period: number; clockSec: number };
  serverLockedAt: string;
  pChoiceAtLock: number;
  modelVersion: string;
  result: 'pending' | 'correct' | 'incorrect' | 'void';
  decidedByEventId?: string;
};

/** Built deterministically from the bounded event ledger; safe to send to a planner. */
export type DirectorEvidence = {
  roomId: string;
  game: Pick<GameMeta, 'gameId' | 'home' | 'away' | 'source' | 'date'>;
  asOf: { seq: number; eventId?: string; period: number; clockSec: number; homeScore: number; awayScore: number; status: 'pre' | 'live' | 'final' };
  profile: DirectorProfile;
  candidates: DirectorMoment[];
  introduction: LocalizedText;
  conclusion: LocalizedText;
  narrationByFocus?: Partial<Record<DirectorLens, DirectorNarration>>;
  call?: DirectorCallEvidence;
  takeIds: string[];
  warnings: string[];
  seenThroughSeq?: number;
};

/** The model selects verified moments and an allowed editorial focus, never new game facts. */
export const DirectorPlanSchema = z.object({
  momentIds: z.array(z.string()).min(1).max(6),
  focus: z.enum(['game', 'team', 'player', 'call', 'reaction']),
  order: z.enum(['chronological', 'climax_first']).optional(),
  reasons: z.array(z.object({
    momentId: z.string(),
    reasonKeys: z.array(z.string().regex(/^[a-z][a-z0-9_]{0,63}$/)).min(1).max(8),
  }).strict()).min(1).max(3).optional(),
}).strict();
export type DirectorPlan = z.infer<typeof DirectorPlanSchema>;
export type DirectorEditorialAudit = {
  policyVersion: 'director.edit.v1';
  mode: 'model' | 'fallback';
  focus: DirectorLens;
  order: DirectorOrder;
  familiarity: DirectorProfile['familiarity'];
  reasonSource: 'model' | 'policy';
  selected: { momentId: string; reasonKeys: string[] }[];
  requiredEventIds: string[];
};
export type DirectorEdit = DirectorNarration & { moments: DirectorMoment[]; editorial: DirectorEditorialAudit };
export type DirectorSegment = { segmentId: string; momentId?: string; eventIds: string[]; text: string };
export type DirectorResponse = {
  directorId: string;
  version: typeof DIRECTOR_VERSION;
  roomId: string;
  game: DirectorEvidence['game'];
  asOf: DirectorEvidence['asOf'];
  language: DirectorLanguage;
  familiarity: DirectorProfile['familiarity'];
  focus: DirectorLens;
  /** Optional so previously saved and published stories remain readable. */
  editorial?: DirectorEditorialAudit;
  /** Public narration is rendered for an observer, with separately generated audio. */
  audience?: { kind: 'public'; author: string };
  moments: DirectorMoment[];
  /** The show settings this script was actually directed with (and the voice's display name when known). */
  show?: DirectorShow & { voiceName?: string };
  /** Present only for model-authored shows. */
  showPlan?: ShowPlan;
  /** True when generated on the flagship `require:'model'` path (never template output). */
  strict?: boolean;
  /** Dev inspector provenance (never shown in consumer UI by default). */
  inspector?: { promptVersion: string; model?: string; requestIds?: string[]; latencyMs?: number; attempts?: number; validation?: string[] };
  segments: DirectorSegment[];
  text: string;
  call?: DirectorCallEvidence;
  takeIds: string[];
  generation: { mode: 'model' | 'fallback'; provider?: string; model?: string; reason?: string; cacheHit: boolean };
  /** Writing is separate from moment selection. Model prose passes bounded rule checks and a
   * second factual review; these checks reduce risk, not a proof of semantic correctness. */
  narration?: { mode: 'model' | 'fallback'; provider?: string; model?: string; reason?: string;
    policyVersion: 'narration.v1'; checks?: 'rules_and_model_review' | 'advisory';
    citations?: { segmentId: string; factIds: string[]; eventIds: string[] }[] };
  provenance: { source: GameMeta['source']; eventIds: string[]; evidenceHash: string; generatedAt: string; warnings: string[] };
  audio: { status: 'not_requested'; speechUrl: string };
};

export type DirectorAudio = {
  directorId: string;
  status: 'ready' | 'unavailable';
  reason?: string;
  disclosure: string;
  provider?: string;
  model?: string;
  voice?: string;
  segments: {
    segmentId: string;
    url: string;
    durationMs: number;
    captions: { startMs: number; endMs: number; text: string }[];
    /** Word timings derived from the provider's character alignment of THIS audio (not guessed). */
    words?: { text: string; startMs: number; endMs: number }[];
  }[];
};

/** POST /director/:id/speech body. voiceId must be in GET /director/voices. */
export const DirectorSpeechRequestSchema = z.object({
  fanId: z.string().min(1).max(64),
  voiceId: z.string().regex(/^[A-Za-z0-9_-]{4,64}$/).optional(),
});

export const PublishDirectorStorySchema = z.object({
  fanId: z.string().min(1).max(64),
  directorId: z.string().regex(/^d-[a-f0-9]{32}$/),
  /** Explicit inclusion; publishing a story never silently publishes a camera take. */
  takeId: z.string().max(80).optional(),
}).strict();
export type PersonalStory = {
  storyId: string;
  publishedAt: string;
  author: { displayName: string };
  director: DirectorResponse;
  take?: Take;
  /** Frontend route; public JSON is /stories/:storyId. */
  sharePath: string;
};
